import { useCallback, useEffect, useState } from 'react';

// Theme preference: 'system' (default — follows the device day/night
// setting live), 'light' or 'dark'. Persisted in localStorage('sg-theme');
// an inline script in index.html applies it before first paint (no flash).
const STORAGE_KEY = 'sg-theme';
const _noopMq = { matches: false, addEventListener: () => {}, removeEventListener: () => {} };
const mq = () => (typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : _noopMq);

export const getThemePreference = () => {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
};

export const applyTheme = (pref) => {
  const dark = pref === 'dark' || (pref === 'system' && mq().matches);
  document.documentElement.classList.toggle('dark', dark);
  return dark;
};

export const useTheme = () => {
  const [preference, setPreferenceState] = useState(getThemePreference);
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark'));

  useEffect(() => {
    setIsDark(applyTheme(preference));
    if (preference !== 'system') return undefined;
    // Live-follow the device day/night switch while in system mode.
    const m = mq();
    const onChange = () => setIsDark(applyTheme('system'));
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((pref) => {
    try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* private mode */ }
    setPreferenceState(pref);
  }, []);

  return { preference, isDark, setPreference };
};
